import { type Block, type FormDefinition, isField } from "@fobu/schema";

/** Ein übersetzbarer Text mit stabilem Pfad (IDs, nicht Positionen – Umsortieren bricht nichts). */
export interface TranslatableText {
  path: string;
  /** Wo steht der Text? Für die Gruppierung im Editor. */
  context: string;
  source: string;
  multiline: boolean;
}

const BLOCK_PROPS = ["label", "description", "text", "consentText", "placeholder", "minLabel", "midLabel", "maxLabel", "alt", "title", "unit", "caption"] as const;

/** Alle Texte eines Formulars, die Ausfüllende sehen – in Anzeigereihenfolge. */
export function translatableTexts(def: FormDefinition): TranslatableText[] {
  const out: TranslatableText[] = [];
  const add = (path: string, context: string, source: string | undefined, multiline = false) => {
    if (source && source.trim()) out.push({ path, context, source, multiline });
  };
  add("title", "Formular", def.title);
  def.pages.forEach((page, pi) => {
    const pctx = `Seite ${pi + 1}`;
    add(`page.${page.id}.title`, pctx, page.title);
    add(`page.${page.id}.description`, pctx, page.description, true);
    for (const b of page.blocks as Block[]) {
      const ctx = isField(b) ? b.label || b.key : pctx;
      for (const prop of BLOCK_PROPS) {
        const v = (b as unknown as Record<string, unknown>)[prop];
        if (typeof v === "string") add(`block.${b.id}.${prop}`, ctx, v, prop === "text" || prop === "description" || prop === "consentText");
      }
      if ("choices" in b) for (const c of b.choices) add(`choice.${b.id}.${c.id}`, ctx, c.label);
      if (b.type === "matrix") {
        for (const r of b.rows) add(`row.${b.id}.${r.id}`, ctx, r.label);
        for (const c of b.columns) add(`col.${b.id}.${c.id}`, ctx, c.label);
      }
      if (b.type === "poll") {
        for (const o of b.options) add(`poll.${b.id}.${o.id}`, ctx, o.label);
        add(`block.${b.id}.location`, ctx, b.location);
      }
      if (b.type === "group") {
        add(`block.${b.id}.itemLabel`, ctx, b.itemLabel);
        add(`block.${b.id}.addLabel`, ctx, b.addLabel);
        for (const sub of b.fields) {
          const sctx = `${ctx} › ${sub.label || sub.key}`;
          for (const prop of BLOCK_PROPS) {
            const v = (sub as unknown as Record<string, unknown>)[prop];
            if (typeof v === "string") add(`block.${sub.id}.${prop}`, sctx, v, prop === "description");
          }
          if ("choices" in sub) for (const c of sub.choices) add(`choice.${sub.id}.${c.id}`, sctx, c.label);
        }
      }
    }
  });
  def.endings.forEach((e, ei) => {
    add(`ending.${e.id}.title`, `Endseite ${ei + 1}`, e.title);
    add(`ending.${e.id}.body`, `Endseite ${ei + 1}`, e.body, true);
    add(`ending.${e.id}.buttonLabel`, `Endseite ${ei + 1}`, e.buttonLabel);
  });
  if (def.settings.closedMessage) add("settings.closedMessage", "Einstellungen", def.settings.closedMessage, true);
  return out;
}

/**
 * Liefert die Definition in einer Sprache. Fehlende Übersetzungen fallen auf die Basissprache zurück.
 * Struktur, IDs, Keys und Logik bleiben unverändert – Antworten sind sprachunabhängig auswertbar.
 */
export function localize(def: FormDefinition, lang: string | undefined): FormDefinition {
  const tr = lang && lang !== def.settings.language ? def.translations[lang] : undefined;
  if (!tr || Object.keys(tr).length === 0) return def;
  const pick = (path: string, fallback: string) => {
    const v = tr[path];
    return v && v.trim() ? v : fallback;
  };
  const opt = (path: string, fallback: string | undefined) => (fallback === undefined ? undefined : pick(path, fallback));

  const clone = JSON.parse(JSON.stringify(def)) as FormDefinition;
  clone.title = pick("title", clone.title);
  clone.settings = { ...clone.settings, language: lang!, ...(clone.settings.closedMessage ? { closedMessage: pick("settings.closedMessage", clone.settings.closedMessage) } : {}) };
  for (const page of clone.pages) {
    if (page.title !== undefined) page.title = pick(`page.${page.id}.title`, page.title);
    if (page.description !== undefined) page.description = pick(`page.${page.id}.description`, page.description);
    for (const b of page.blocks) {
      const rec = b as unknown as Record<string, unknown>;
      for (const prop of BLOCK_PROPS) if (typeof rec[prop] === "string") rec[prop] = pick(`block.${b.id}.${prop}`, rec[prop] as string);
      if ("choices" in b) for (const c of b.choices) c.label = pick(`choice.${b.id}.${c.id}`, c.label);
      if (b.type === "matrix") {
        for (const r of b.rows) r.label = pick(`row.${b.id}.${r.id}`, r.label);
        for (const c of b.columns) c.label = pick(`col.${b.id}.${c.id}`, c.label);
      }
      if (b.type === "poll") {
        for (const o of b.options) o.label = pick(`poll.${b.id}.${o.id}`, o.label);
        if (b.location !== undefined) b.location = pick(`block.${b.id}.location`, b.location);
      }
      if (b.type === "group") {
        b.itemLabel = pick(`block.${b.id}.itemLabel`, b.itemLabel);
        if (b.addLabel !== undefined) b.addLabel = pick(`block.${b.id}.addLabel`, b.addLabel);
        for (const sub of b.fields) {
          const srec = sub as unknown as Record<string, unknown>;
          for (const prop of BLOCK_PROPS) if (typeof srec[prop] === "string") srec[prop] = pick(`block.${sub.id}.${prop}`, srec[prop] as string);
          if ("choices" in sub) for (const c of sub.choices) c.label = pick(`choice.${sub.id}.${c.id}`, c.label);
        }
      }
    }
  }
  for (const e of clone.endings) {
    e.title = pick(`ending.${e.id}.title`, e.title);
    const body = opt(`ending.${e.id}.body`, e.body);
    if (body !== undefined) e.body = body;
    const btn = opt(`ending.${e.id}.buttonLabel`, e.buttonLabel);
    if (btn !== undefined) e.buttonLabel = btn;
  }
  return clone;
}

/** Wie vollständig ist eine Sprache? */
export function translationProgress(def: FormDefinition, lang: string): { done: number; total: number; missing: string[] } {
  const texts = translatableTexts(def);
  const tr = def.translations[lang] ?? {};
  const missing = texts.filter((t) => !tr[t.path]?.trim()).map((t) => t.path);
  return { done: texts.length - missing.length, total: texts.length, missing };
}

/** Wählt die passende Sprache: URL-Parameter, dann Browser-Sprachen, sonst Basissprache. */
export function pickLanguage(def: FormDefinition, requested: string | null | undefined, browser: readonly string[] = []): string {
  const available = [def.settings.language, ...(def.settings.languages ?? [])];
  const norm = (l: string) => l.toLowerCase().split("-")[0]!;
  for (const cand of [requested, ...browser]) {
    if (!cand) continue;
    const hit = available.find((a) => norm(a) === norm(cand));
    if (hit) return hit;
  }
  return def.settings.language;
}
